import { createContext, useCallback, useContext, useEffect, useRef, useState, type PropsWithChildren } from "react";

export type ConfirmDialogOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "default";
};

type ConfirmationRequest = {
  options: Required<ConfirmDialogOptions>;
  resolve: (confirmed: boolean) => void;
  returnFocus: HTMLElement | null;
};

type ConfirmDialogContextValue = {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

function Dialog({ request, onResolve }: { request: ConfirmationRequest; onResolve(confirmed: boolean): void }) {
  const dialog = useRef<HTMLElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => cancelButton.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onResolve(false); return; }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>("button:not(:disabled)") ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", keydown); };
  }, [onResolve]);

  return <div className="confirm-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onResolve(false); }}>
    <section ref={dialog} className={`confirm-dialog confirm-dialog--${request.options.tone}`} role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description">
      <header><span aria-hidden="true">{request.options.tone === "danger" ? "!" : "?"}</span><div><small>Confirmación requerida</small><h2 id="confirm-dialog-title">{request.options.title}</h2></div></header>
      <p id="confirm-dialog-description">{request.options.description}</p>
      <footer><button ref={cancelButton} type="button" className="button button--ghost" onClick={() => onResolve(false)}>{request.options.cancelLabel}</button><button type="button" className={`button ${request.options.tone === "danger" ? "button--danger-solid" : "button--primary"}`} onClick={() => onResolve(true)}>{request.options.confirmLabel}</button></footer>
    </section>
  </div>;
}

export function ConfirmDialogProvider({ children }: PropsWithChildren) {
  const [request, setRequest] = useState<ConfirmationRequest | null>(null);
  const confirm = useCallback((input: ConfirmDialogOptions) => new Promise<boolean>((resolveRequest) => {
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRequest((current) => {
      current?.resolve(false);
      return {
        options: { confirmLabel: "Confirmar", cancelLabel: "Cancelar", tone: "default", ...input },
        resolve: resolveRequest,
        returnFocus
      };
    });
  }), []);
  const resolve = useCallback((confirmed: boolean) => {
    setRequest((current) => {
      if (!current) return null;
      current.resolve(confirmed);
      requestAnimationFrame(() => current.returnFocus?.focus());
      return null;
    });
  }, []);

  return <ConfirmDialogContext.Provider value={{ confirm }}>
    {children}
    {request && <Dialog request={request} onResolve={resolve} />}
  </ConfirmDialogContext.Provider>;
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) throw new Error("ConfirmDialogProvider no está configurado.");
  return context.confirm;
}

export function useOptionalConfirmDialog() {
  return useContext(ConfirmDialogContext)?.confirm;
}
